import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { format } from "date-fns";
import {
  IconTile,
  KpiCard,
  KpiGrid,
  SA,
  SaCard,
  SaPageHeader,
  Tone,
  saCardSx,
  saTableBodySx,
  saTableHeadSx,
} from "../../components/superadmin/saUi";
import {
  OverviewOrgRow,
  PLATFORM_KEYS,
  PLATFORM_LABELS,
  PlatformKey,
  PlatformOverview,
  SubscriptionStatus,
  SUBSCRIPTION_STATUS_LABELS,
  getPlatformOverview,
} from "../../services/superadminService";
import {
  PaymentStatusChip,
  PlatformChip,
  SubscriptionStatusChip,
  formatMoney,
} from "../../components/superadmin/SubscriptionChips";

/**
 * Chart tokens. The two series take categorical slots 1 and 2 in fixed order
 * (validated on the white card: CVD ΔE 24.7, contrast ≥ 3:1). Colour follows
 * the platform, never its rank, so a month without clinics keeps Sports blue.
 */
const SERIES_COLORS: Record<PlatformKey, string> = {
  SPORTS: "#2a78d6",
  CLINIC: "#eb6834",
};
const INK = { primary: "#0b0b0b", secondary: "#52514e", muted: "#898781" };
const GRID = "#e1e0d9";
const SURFACE = "#ffffff";

/** Status tiles carry an icon + label, so colour never works alone. */
const STATUS_TILES: {
  status: SubscriptionStatus;
  icon: React.ReactNode;
  tone: Tone;
  caption: string;
}[] = [
  {
    status: "TRIAL",
    icon: <ScheduleIcon />,
    tone: "blue",
    caption: "on a free trial",
  },
  {
    status: "ACTIVE",
    icon: <CheckCircleOutlineIcon />,
    tone: "green",
    caption: "paid, more than a week left",
  },
  {
    status: "EXPIRING",
    icon: <WarningAmberIcon />,
    tone: "amber",
    caption: "paid, ending within a week",
  },
  {
    status: "GRACE",
    icon: <HourglassBottomIcon />,
    tone: "amber",
    caption: "ended, in the 3 days before lock",
  },
  {
    status: "EXPIRED",
    icon: <ErrorOutlineIcon />,
    tone: "red",
    caption: "locked out, needs renewal",
  },
  {
    status: "UNLIMITED",
    icon: <AllInclusiveIcon />,
    tone: "slate",
    caption: "no end date",
  },
];

const tileSx = {
  ...saCardSx,
  p: 2,
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minWidth: 0,
} as const;

const clickableTileSx = {
  ...tileSx,
  cursor: "pointer",
  transition: "border-color 150ms, box-shadow 150ms",
  "&:hover": { borderColor: "#CBD5E1", boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)" },
  "&:focus-visible": { outline: `2px solid ${SA.accent}`, outlineOffset: 2 },
} as const;

const TileLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: "0.84rem", fontWeight: 500, color: SA.textMuted }}>{children}</Typography>
);

const TileValue: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontSize: "1.75rem",
      fontWeight: 700,
      lineHeight: 1.1,
      color: SA.text,
      fontVariantNumeric: "tabular-nums",
      letterSpacing: "-0.01em",
    }}
  >
    {children}
  </Typography>
);

const TileCaption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: "0.8rem", color: SA.textMuted }}>{children}</Typography>
);

const monthLabel = (key: string) => format(new Date(`${key}-01T00:00:00`), "MMM");
const monthLongLabel = (key: string) =>
  format(new Date(`${key}-01T00:00:00`), "MMMM yyyy");

const SignupsTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((sum: number, p: any) => sum + (p.value ?? 0), 0);
  return (
    <Paper elevation={3} sx={{ px: 1.5, py: 1, minWidth: 150 }}>
      <Typography sx={{ fontSize: "0.78rem", color: INK.secondary, mb: 0.5 }}>
        {monthLongLabel(label)}
      </Typography>
      {[...payload].reverse().map((p: any) => (
        <Box key={p.dataKey} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 12, height: 2, bgcolor: p.color, borderRadius: 1 }} />
          <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: INK.primary }}>
            {p.value}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: INK.secondary }}>
            {PLATFORM_LABELS[p.dataKey as PlatformKey]}
          </Typography>
        </Box>
      ))}
      <Typography sx={{ fontSize: "0.78rem", color: INK.secondary, mt: 0.5 }}>
        {total} total
      </Typography>
    </Paper>
  );
};

const OrgList: React.FC<{
  title: string;
  empty: string;
  rows: OverviewOrgRow[];
  icon: React.ReactNode;
  tone: Tone;
}> = ({ title, empty, rows, icon, tone }) => {
  const navigate = useNavigate();
  return (
    <SaCard title={title} icon={icon} tone={tone} flush sx={{ minWidth: 0 }}>
      {rows.length === 0 ? (
        <Typography sx={{ px: 2.5, py: 2, fontSize: "0.88rem", color: SA.textMuted }}>
          {empty}
        </Typography>
      ) : (
        rows.map((row, index) => (
          <Box key={row.id}>
            {index > 0 && <Divider />}
            <Box
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/superadmin/organizations/${row.id}`)}
              onKeyDown={(e) => {
                if (e.key === "Enter") navigate(`/superadmin/organizations/${row.id}`);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2.5,
                py: 1.25,
                minHeight: 48,
                cursor: "pointer",
                "&:hover, &:focus-visible": { bgcolor: SA.surfaceMuted },
              }}
            >
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: SA.text }} noWrap>
                  {row.name}
                </Typography>
                <Typography sx={{ fontSize: "0.76rem", color: SA.textMuted }}>
                  {row.plan?.name ?? (row.status === "TRIAL" ? "Free trial" : "No package")}
                  {row.endsAt ? ` · ${format(new Date(row.endsAt), "d MMM")}` : ""}
                </Typography>
              </Box>
              <PlatformChip platform={row.platform} />
              <SubscriptionStatusChip
                summary={{
                  status: row.status,
                  daysLeft: row.daysLeft,
                  endsAt: row.endsAt,
                  isTrial: row.status === "TRIAL",
                  plan: row.plan,
                }}
              />
            </Box>
          </Box>
        ))
      )}
    </SaCard>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="h2"
    sx={{
      fontSize: "0.75rem",
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: SA.textSubtle,
      mt: 2.5,
      mb: 1,
      "&:first-of-type": { mt: 0 },
    }}
  >
    {children}
  </Typography>
);

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<PlatformOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTable, setShowTable] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getPlatformOverview());
    } catch {
      // The interceptor already surfaced the message.
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        {loading ? <CircularProgress size={28} /> : <Typography>Could not load the overview.</Typography>}
      </Box>
    );
  }

  const totals = PLATFORM_KEYS.reduce(
    (acc, key) => {
      const counts = data.byPlatform[key];
      acc.total += counts.total;
      for (const tile of STATUS_TILES) acc[tile.status] += counts[tile.status];
      return acc;
    },
    { total: 0, TRIAL: 0, ACTIVE: 0, EXPIRING: 0, GRACE: 0, EXPIRED: 0, UNLIMITED: 0 } as Record<
      SubscriptionStatus | "total",
      number
    >
  );
  const signupsThisYear = data.signupsByMonth.reduce(
    (sum, row) => sum + PLATFORM_KEYS.reduce((s, key) => s + row[key], 0),
    0
  );

  return (
    // Refetch keeps the frame: the previous render dims instead of flashing.
    <Box sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 150ms" }}>
      <SaPageHeader
        title="Overview"
        subtitle="Active organizations across both platforms. Deleted ones are left out."
        icon={<SpaceDashboardOutlinedIcon />}
        actions={
          <Tooltip title="Refresh">
            <IconButton onClick={load} aria-label="Refresh overview">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />

      {/* Platform split */}
      <SectionLabel>Organizations</SectionLabel>
      <KpiGrid min={200}>
        <KpiCard
          label="Organizations"
          icon={<ApartmentOutlinedIcon />}
          tone="slate"
          value={totals.total}
          caption={`${signupsThisYear} signed up in the last 12 months`}
        />
        {PLATFORM_KEYS.map((key) => (
          <Paper
            key={key}
            elevation={0}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/superadmin/organizations?platform=${key}`)}
            onKeyDown={(e) => {
              if (e.key === "Enter") navigate(`/superadmin/organizations?platform=${key}`);
            }}
            sx={clickableTileSx}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box
                aria-hidden
                sx={{ width: 32, height: 32, borderRadius: "10px", display: "grid", placeItems: "center", bgcolor: `${SERIES_COLORS[key]}1A` }}
              >
                <Box sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: SERIES_COLORS[key] }} />
              </Box>
              <TileLabel>{PLATFORM_LABELS[key]}</TileLabel>
            </Box>
            <TileValue>{data.byPlatform[key].total}</TileValue>
            <TileCaption>
              {data.byPlatform[key].TRIAL} trial · {data.byPlatform[key].EXPIRED} expired
            </TileCaption>
          </Paper>
        ))}
      </KpiGrid>

      {/* Subscription status */}
      <SectionLabel>Subscriptions</SectionLabel>
      <KpiGrid min={170}>
        {STATUS_TILES.map((tile) => (
          <KpiCard
            key={tile.status}
            label={SUBSCRIPTION_STATUS_LABELS[tile.status]}
            icon={tile.icon}
            tone={tile.tone}
            value={totals[tile.status]}
            caption={tile.caption}
          />
        ))}
      </KpiGrid>

      {/* Payments (Whish) */}
      <SectionLabel>Payments</SectionLabel>
      <KpiGrid min={200}>
        {(data.payments.totals.length ? data.payments.totals : [{ currency: "USD", thisMonth: 0, last12Months: 0, count: 0 }]).map(
          (total) => (
            <KpiCard
              key={total.currency}
              label="Paid this month"
              icon={<PaymentsOutlinedIcon />}
              tone="green"
              value={formatMoney(total.thisMonth, total.currency)}
              caption={`${formatMoney(total.last12Months, total.currency)} over 12 months · ${total.count} payment${total.count === 1 ? "" : "s"}`}
            />
          )
        )}
        <Paper
          elevation={0}
          role="button"
          tabIndex={0}
          onClick={() => navigate("/superadmin/payments?status=PENDING")}
          onKeyDown={(e) => {
            if (e.key === "Enter") navigate("/superadmin/payments?status=PENDING");
          }}
          sx={clickableTileSx}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <IconTile icon={<HourglassTopOutlinedIcon />} tone="amber" size={32} />
            <TileLabel>Pending payments</TileLabel>
          </Box>
          <TileValue>{data.payments.pendingCount}</TileValue>
          <TileCaption>
            {data.payments.mode === "demo"
              ? "Whish in demo mode: no real money"
              : "started in Whish, not confirmed yet"}
          </TileCaption>
        </Paper>
      </KpiGrid>

      {/* Signups per month */}
      <SaCard
        title="New signups per month"
        subtitle="Last 12 months, by platform"
        icon={<BarChartOutlinedIcon />}
        sx={{ mt: 3, mb: 2.5 }}
        action={
          <Button size="small" onClick={() => setShowTable((prev) => !prev)} sx={{ textTransform: "none", fontWeight: 600 }}>
            {showTable ? "Show chart" : "Show as table"}
          </Button>
        }
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}>
          {/* Legend: always present for two series. */}
          <Box sx={{ display: "flex", gap: 2 }}>
            {PLATFORM_KEYS.map((key) => (
              <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: SERIES_COLORS[key] }} />
                <Typography sx={{ fontSize: "0.82rem", color: INK.secondary }}>
                  {PLATFORM_LABELS[key]}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {showTable ? (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead sx={saTableHeadSx}>
                <TableRow>
                  <TableCell>Month</TableCell>
                  {PLATFORM_KEYS.map((key) => (
                    <TableCell key={key} align="right">
                      {PLATFORM_LABELS[key]}
                    </TableCell>
                  ))}
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={saTableBodySx}>
                {data.signupsByMonth.map((row) => (
                  <TableRow key={row.month}>
                    <TableCell>{monthLongLabel(row.month)}</TableCell>
                    {PLATFORM_KEYS.map((key) => (
                      <TableCell key={key} align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {row[key]}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                      {PLATFORM_KEYS.reduce((sum, key) => sum + row[key], 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Box sx={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.signupsByMonth} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID} strokeWidth={1} />
                <XAxis
                  dataKey="month"
                  tickFormatter={monthLabel}
                  tick={{ fill: INK.muted, fontSize: 12 }}
                  axisLine={{ stroke: "#c3c2b7" }}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: INK.muted, fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <ChartTooltip
                  content={<SignupsTooltip />}
                  cursor={{ fill: "rgba(11,11,11,0.04)" }}
                />
                {PLATFORM_KEYS.map((key, index) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    stackId="signups"
                    fill={SERIES_COLORS[key]}
                    maxBarSize={24}
                    // The 2px surface-coloured edge is the gap between segments.
                    stroke={SURFACE}
                    strokeWidth={2}
                    radius={index === PLATFORM_KEYS.length - 1 ? [4, 4, 0, 0] : 0}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </SaCard>

      <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" } }}>
        <OrgList
          title={`Ending within ${data.expiringWindowDays} days`}
          empty="Nobody's trial or subscription ends this week."
          rows={data.endingSoon}
          icon={<HourglassTopOutlinedIcon />}
          tone="amber"
        />
        <OrgList
          title="Expired in the last 30 days"
          empty="No subscription has lapsed recently."
          rows={data.recentlyExpired}
          icon={<EventBusyOutlinedIcon />}
          tone="red"
        />
      </Box>

      <SaCard
        title="Recent payments"
        icon={<ReceiptLongOutlinedIcon />}
        tone="green"
        flush
        sx={{ mt: 2.5 }}
        action={
          <Button size="small" onClick={() => navigate("/superadmin/payments")} sx={{ textTransform: "none", fontWeight: 600 }}>
            See all
          </Button>
        }
      >
        {data.payments.recent.length === 0 ? (
          <Typography sx={{ px: 2.5, py: 2, fontSize: "0.88rem", color: SA.textMuted }}>
            No payments yet.
          </Typography>
        ) : (
          data.payments.recent.map((payment, index) => (
            <Box key={payment.id}>
              {index > 0 && <Divider />}
              <Box
                role="button"
                tabIndex={0}
                onClick={() =>
                  payment.organization?.id &&
                  navigate(`/superadmin/organizations/${payment.organization.id}`)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && payment.organization?.id)
                    navigate(`/superadmin/organizations/${payment.organization.id}`);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2.5,
                  py: 1.25,
                  minHeight: 48,
                  cursor: "pointer",
                  "&:hover, &:focus-visible": { bgcolor: SA.surfaceMuted },
                }}
              >
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      color: payment.organization?.deleted ? "#A32B22" : undefined,
                    }}
                    noWrap
                  >
                    {payment.organization?.name ?? "—"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.76rem", color: SA.textMuted }}>
                    #{payment.invoiceNumber} · {payment.planName} ·{" "}
                    {format(new Date(payment.paidAt ?? payment.createdAt), "d MMM, HH:mm")}
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", fontVariantNumeric: "tabular-nums" }}>
                  {formatMoney(payment.amount, payment.currency)}
                </Typography>
                <PaymentStatusChip status={payment.status} />
              </Box>
            </Box>
          ))
        )}
      </SaCard>
    </Box>
  );
};

export default Overview;
